import { WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css, html } from 'lit';
import { property } from 'lit/decorators.js';

import type { SelectionModifiers } from './types.js';

/**
 * View-agnostic per-row drag handle — the 4 × 12 px grab pill that
 * sits at the left edge of both Table and Gantt label rows. Click
 * forwards to an `onSelect(mods)` callback (same shape as
 * `dv-row-select-checkbox`) so the shared `RowSelectionController`
 * handles Shift/Ctrl/Meta semantics.
 *
 * Actual row-reorder drag is NOT wired here: different views start
 * reorder through different channels (Table uses BlockSuite
 * `handleEvent('dragStart')`, Gantt listens for pointermove on the
 * label row). The element carries the `data-view-row-drag-handle`
 * class so those higher-level handlers can identify it via
 * `target.closest(...)` without re-implementing the visuals.
 */
export class DvRowDragHandle extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    dv-row-drag-handle {
      display: inline-flex;
    }
    .dv-row-drag-handle {
      width: 4px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: grab;
      /* Solid surface bg instead of transparent — rows in gantt
         sit on a group-tint wash where a transparent handle pill
         would blend in. The var resolves to white in the light /
         blue-white viewport scopes and to the dark surface in
         dark themes, matching the row's idle bg. */
      background: var(--streamlain-background-primary-color);
    }
    .dv-row-drag-handle-pill {
      width: 4px;
      border-radius: 2px;
      height: 12px;
      background-color: var(--streamlain-placeholder-color);
    }
  `;

  @property({ attribute: false })
  accessor onSelect: ((mods: SelectionModifiers) => void) | undefined;

  override connectedCallback() {
    super.connectedCallback();
    this.disposables.addFromEvent(this, 'click', (e: MouseEvent) => {
      e.stopPropagation();
      this.onSelect?.({
        shift: e.shiftKey,
        ctrl: e.ctrlKey,
        meta: e.metaKey,
      });
    });
  }

  override render() {
    return html`<div class="dv-row-drag-handle data-view-row-drag-handle">
      <div class="dv-row-drag-handle-pill"></div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-row-drag-handle': DvRowDragHandle;
  }
}
